import { BaseColumns, SelectOptions } from '@/model/CommonModel';
import { Dropdown, MenuProps } from 'antd';
import cx from 'classnames';
import React from 'react';
import styles from './rightClickableRow.module.scss';

type RightClickableRowProps<T> = {
	record: T;
	children?: React.ReactNode;
	options?: SelectOptions<T>[];
	rowClassName?: string;
	onClickOption?: (value: T) => void;
	onChangeSelection?: (value: T) => void;
} & React.HTMLAttributes<HTMLElement>;

const RightClickableRow = <T extends BaseColumns | {}>({
	record,
	children,
	options,
	rowClassName,
	onClickOption,
	onChangeSelection,
	...restProps
}: RightClickableRowProps<T>) => {
	let content: JSX.Element;

	const createMenuItems = (options: SelectOptions<T>[]): MenuProps['items'] =>
		options.map(({ label, disabled, title, icon, id, separator, value, nestedOptions }, index) => ({
			className: separator ? styles.separator : '',
			disabled,
			icon,
			id,
			key: `${label}-${index}`,
			label: (
				<span
					className={styles.internalSpan}
					onClick={e => {
						if (disabled) e.stopPropagation();
					}}>
					{label}
				</span>
			),
			onClick: e => {
				e.domEvent.stopPropagation();
				onClickOption?.(value);
			},
			title,
			children: nestedOptions ? createMenuItems(nestedOptions) : undefined,
		}));

	if (options) {
		content = (
			<Dropdown
				menu={{ items: createMenuItems(options) }}
				placement='bottomRight'
				trigger={['contextMenu']}
				destroyPopupOnHide
				onOpenChange={visible => {
					if (visible) {
						onChangeSelection?.(record);
					}
				}}
				overlayStyle={{ width: 'max-content' }}>
				<tr {...restProps} className={cx(restProps.className, rowClassName)}>
					{children}
				</tr>
			</Dropdown>
		);
	} else {
		content = (
			<tr {...restProps} className={cx(restProps.className, rowClassName)}>
				{children}
			</tr>
		);
	}

	return content;
};

export { RightClickableRow };
